/* El gráfico del asistente. ECharts, cargado a demanda.
 *
 * Tres decisiones que conviene no deshacer:
 *
 * 1. `import()` DINÁMICO. ECharts pesa ~1 MB; la inmensa mayoría de las sesiones no ve un solo
 *    gráfico. Con la importación dinámica, Vite lo saca a su propio trozo y solo se descarga
 *    cuando el modelo llama de verdad a `graficar`.
 *
 * 2. SE EMPAQUETA, no se carga de otro host. El frontend vive en `app.<dominio>` y la API en
 *    `api.<dominio>`; un `<script src>` a la API —que es lo que hacía el chat anterior— lo bloquea
 *    la CSP de producción, y el gráfico desaparece justo donde nadie está mirando.
 *
 * 3. LOS FORMATEADORES SON NUESTROS. El `option` que manda el servidor es JSON puro y NO trae
 *    funciones (no podría: viaja por SSE). Las de eje y de globo se inyectan aquí, con
 *    `formato.ts`, para que un gráfico de dinero diga «$24.600 millones» y no «2.46e10». Es la
 *    misma regla de cifras del resto del producto, aplicada donde se dibuja.
 */
import { useEffect, useRef, useState } from 'react'
import { pesos, entero } from '../api/formato'
import { urlApi } from '../api/cliente'
import './Grafico.css'

export interface MetaGrafico {
  tipo?: string
  titulo?: string
  subtitulo?: string
  /** Los valores son pesos: cambia los formateadores de eje y de globo. */
  dinero?: boolean
  escala_tamano?: boolean
}

/** La paleta del rediseño, como tema de ECharts. El tema `cairo` del chat anterior es el de
 *  «papel periódico» —crema y rojo— y aquí desentonaría con todo lo demás. */
const TEMA = {
  color: ['#2a6b73', '#4a7f60', '#9a6a1a', '#a33a2c', '#35858e', '#7d5714', '#7da78c'],
  backgroundColor: 'transparent',
  textStyle: { fontFamily: 'Manrope, system-ui, sans-serif', color: '#3c5257' },
  title: { textStyle: { color: '#17333a', fontWeight: 600 } },
  legend: { textStyle: { color: '#526468' } },
  tooltip: {
    backgroundColor: '#ffffff',
    borderColor: '#c2d099',
    borderWidth: 1,
    textStyle: { color: '#17333a', fontSize: 13 },
    extraCssText: 'box-shadow:0 4px 18px rgba(23,51,58,.14);',
  },
  categoryAxis: {
    axisLine: { lineStyle: { color: '#c2d099' } },
    axisLabel: { color: '#526468' },
    splitLine: { lineStyle: { color: '#dbe3c6' } },
  },
  valueAxis: {
    axisLine: { lineStyle: { color: '#c2d099' } },
    axisLabel: { color: '#526468' },
    splitLine: { lineStyle: { color: '#dbe3c6' } },
  },
  visualMap: { textStyle: { color: '#526468' }, inRange: { color: ['#eaf1de', '#7da78c', '#2a6b73'] } },
}

type Echarts = typeof import('echarts')
let cargando: Promise<Echarts> | null = null

function cargarEcharts(): Promise<Echarts> {
  if (!cargando) {
    cargando = import('echarts').then((mod) => {
      mod.registerTheme('vigia', TEMA)
      return mod
    })
  }
  return cargando
}

let mapaListo: Promise<void> | null = null

/** El coroplético necesita el GeoJSON de departamentos, que sirve la API. Se registra UNA vez. */
function cargarMapa(ec: Echarts): Promise<void> {
  if (!mapaListo) {
    mapaListo = fetch(urlApi('/static/co_departamentos.json'), { credentials: 'include' })
      .then((r) => r.json())
      .then((geo) => { ec.registerMap('colombia', geo) })
      .catch(() => { /* sin mapa: el gráfico sale vacío pero el turno no se cae */ })
  }
  return mapaListo
}

export function Grafico({ option, meta }: { option: unknown; meta: MetaGrafico }) {
  const caja = useRef<HTMLDivElement>(null)
  const [fallo, setFallo] = useState(false)

  useEffect(() => {
    let vivo = true
    let instancia: { dispose: () => void; resize: () => void } | null = null
    const alRedimensionar = () => instancia?.resize()

    void (async () => {
      try {
        const ec = await cargarEcharts()
        if (meta.tipo === 'mapa') await cargarMapa(ec)
        if (!vivo || !caja.current) return
        const g = ec.init(caja.current, 'vigia', { renderer: 'canvas' })
        g.setOption(conFormateadores(option, meta) as Parameters<typeof g.setOption>[0])
        instancia = g
        window.addEventListener('resize', alRedimensionar)
      } catch {
        if (vivo) setFallo(true)
      }
    })()

    return () => {
      vivo = false
      window.removeEventListener('resize', alRedimensionar)
      instancia?.dispose()
    }
  }, [option, meta])

  if (fallo) {
    // Se dice, no se esconde: el modelo ya afirmó en su respuesta que el lector «ya lo ve».
    return <p className="grafico-fallo">No pude dibujar el gráfico. Las cifras están en el texto.</p>
  }
  return (
    <figure className="grafico">
      {meta.titulo && <figcaption>{meta.titulo}</figcaption>}
      {meta.subtitulo && <p className="grafico-sub">{meta.subtitulo}</p>}
      <div ref={caja} className="grafico-lienzo" />
    </figure>
  )
}

/**
 * Mete NUESTROS formateadores en el `option`. Sin esto los ejes salen en notación científica y el
 * globo con el número crudo — que es exactamente lo que la regla de cifras del proyecto prohíbe.
 */
function conFormateadores(option: unknown, meta: MetaGrafico): Record<string, unknown> {
  const o = { ...(option as Record<string, unknown>) }
  const corto = (v: unknown) => {
    const n = typeof v === 'number' ? v : Number(v)
    if (!Number.isFinite(n)) return String(v ?? '')
    return meta.dinero ? pesos(n, '—') : entero(n)
  }
  const ejes = (clave: string) => {
    const eje = o[clave]
    if (!eje) return
    const lista = Array.isArray(eje) ? eje : [eje]
    for (const e of lista as Record<string, unknown>[]) {
      if (e && e['type'] === 'value') {
        e['axisLabel'] = { ...(e['axisLabel'] as object), formatter: corto }
      }
    }
  }
  ejes('xAxis')
  ejes('yAxis')
  const globo = (o['tooltip'] ?? {}) as Record<string, unknown>
  o['tooltip'] = {
    ...globo,
    valueFormatter: corto,
  }
  return o
}
